<template>
  <section v-if="allowed" class="customer-health" :class="{ 'customer-health--embedded': embedded }" :aria-label="title">
    <header v-if="!embedded"><div><small>MEINE ENTWICKLUNG</small><h3>{{ title }}</h3></div><v-btn icon dark aria-label="Angezeigte Werte auswählen" @click="settingsOpen = true"><v-icon>mdi-tune</v-icon></v-btn></header>
    <MeScanBody v-if="topic === 'body'" :day="to" :visible-metrics="visibleBodyMetrics" :show-refresh="false" @settings="openSettings" @history-access="authorizeHistory" />
    <template v-else>
      <p v-if="!android" class="customer-health__note">Gesundheitsdaten stehen hier über die Android-App zur Verfügung. Deine Roody-Trainings, Kurse und MeScan bleiben nutzbar.</p>
      <div v-else-if="!health.enabled && !hintDismissed" class="customer-health__hint">
        <p>Verbinde Health Connect für {{ topic === 'movement' ? 'Schritte, Distanz und Energie' : 'Schlaf und Herzwerte' }}. Du entscheidest, welche Daten Roody lesen darf.</p>
        <v-btn small outlined dark :loading="health.busy" @click="openSettings">Gesundheitsdaten verbinden</v-btn>
        <button type="button" class="customer-health__quiet" @click="dismissHint">Hinweis ausblenden</button>
      </div>
      <template v-else-if="health.enabled">
        <v-progress-linear v-if="metrics.busy || refreshing" indeterminate color="primary" height="2" />
        <p v-if="metrics.error" role="alert">{{ metrics.error }} <button type="button" class="customer-health__quiet" @click="reload">Erneut laden</button></p>
        <div v-if="series.length" class="customer-health__values">
          <button v-for="item in visibleSeries" :key="item.key" type="button" @click="openMetric(item)">
            <span>{{ item.label }}</span><strong>{{ format(item.metric, item.latest.value) }} <small>{{ displayUnit(item.metric) }}</small></strong>
            <small>{{ item.sourceName }} · {{ item.methodLabel }}</small><time>{{ date(item.latest.time || item.latest.day) }}</time>
          </button>
        </div>
        <p v-if="series.length && !visibleSeries.length">Du hast diese Werte ausgeblendet. Über die Einstellungen kannst du sie wieder anzeigen.</p>
        <p v-else-if="!series.length && !metrics.busy && !refreshing && !metrics.error">Für diesen Zeitraum liegen noch keine freigegebenen Messwerte vor. Fehlende Werte sind keine Nullwerte.</p>
        <p class="customer-health__note">{{ metrics.coverage.message }}</p>
      </template>
      <p v-if="connectionNote" class="customer-health__note" role="status">{{ connectionNote }}</p>
    </template>

    <v-dialog v-model="settingsOpen" max-width="540" scrollable dark><v-card class="customer-health__detail"><v-card-title>Anzeige anpassen<v-spacer /><v-btn icon aria-label="Anzeige-Einstellungen schliessen" @click="settingsOpen = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title><v-card-text><CustomerHubDisplaySettings :topic="topic" /><v-btn v-if="android" text block class="mt-4" @click="settingsOpen = false; openSettings()">Health-Verbindung verwalten</v-btn></v-card-text></v-card></v-dialog>
    <v-dialog v-model="detailOpen" max-width="800" scrollable dark>
      <v-card class="customer-health__detail"><v-card-title>{{ selectedSeries ? selectedSeries.label : 'Dein Verlauf' }}<v-spacer /><v-btn icon aria-label="Verlauf schliessen" @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn></v-card-title>
        <v-card-text v-if="selectedSeries">
          <p>{{ selectedSeries.sourceName }} · {{ selectedSeries.methodLabel }}</p>
          <svg v-if="chart.dots.length" class="customer-health__chart" viewBox="0 0 320 154" role="img" :aria-label="selectedSeries.label + ': ' + chart.dots.length + ' Messungen'"><polyline v-if="chart.dots.length > 1" :points="chart.line" /><circle v-for="(point, index) in chart.dots" :key="index" :cx="point.x" :cy="point.y" r="3"><title>{{ date(point.time) }}: {{ format(selectedSeries.metric, point.value) }} {{ displayUnit(selectedSeries.metric) }}</title></circle></svg>
          <template>
            <ol class="customer-health__records"><li v-for="(point, index) in selectedSeries.points.slice().reverse()" :key="index"><span>{{ date(point.time || point.day) }}<small v-if="point.partial">Tag noch nicht vollständig</small></span><strong>{{ format(selectedSeries.metric, point.value) }} {{ displayUnit(selectedSeries.metric) }}</strong></li></ol>
          </template>
          <p class="customer-health__note">Diese Reihe enthält ausschliesslich {{ selectedSeries.sourceName }} · {{ selectedSeries.methodLabel }}. Fehlende Messungen werden nicht ergänzt.</p>
        </v-card-text>
      </v-card>
    </v-dialog>
    <AndroidHealthCard v-if="android" ref="connectionControls" settings-only />
  </section>
</template>

<script>
import Vue from "vue"
import customerHubPreferences from "@/services/customerHubPreferences"
import ownHealthFeatureAccess from "@/services/ownHealthFeatureAccess"
import adapter from "@/services/customerHubHealthAdapter"
import healthConnect from "@/services/healthConnectService"
import mescanService from "@/services/mescanHealthService"
import { healthDay, healthMetricValue, HEALTH_METRICS } from "@/services/healthStatistics"
import { BODY_METRICS, bodyValue, bodyChart } from "@/services/mescanHealthData"
import AndroidHealthCard from "./AndroidHealthCard.vue"
import CustomerHubDisplaySettings from "./CustomerHubDisplaySettings.vue"
import displayPreferences from "@/services/customerHubDisplayPreferences"
import { customerHubHealthLocation } from "@/services/customerHubNavigation"
import MeScanBody from "./MeScanBody.vue"

const connectionHint = Vue.observable({ owner: "", hidden: false })

export default {
  name: "CustomerHubHealthPanel",
  components: { AndroidHealthCard, CustomerHubDisplaySettings, MeScanBody },
  props: { embedded: Boolean, topic: { type: String, default: "movement" }, from: { type: String, default: () => `${healthDay().slice(0, 7)}-01` }, to: { type: String, default: () => healthDay() } },
  data: () => ({ health: healthConnect.state, metrics: adapter.metricState, connectionHint, detailOpen: false, settingsOpen: false, selectedSeries: null,
    selectedDay: healthDay(), connectionNote: "", refreshing: false, requestVersion: 0 }),
  computed: {
    allowed() { return customerHubPreferences.isEnabled() && ownHealthFeatureAccess.isAllowed() },
    android() { return healthConnect.isAndroid() },
    owner() { return customerHubPreferences.state.userId },
    hintDismissed() { return this.connectionHint.owner === this.owner && this.connectionHint.hidden },
    title() { return { movement: "Bewegung im Alltag", "sleep-heart": "Schlaf & Herzwerte", body: "Körper & MeScan" }[this.topic] || "Gesundheitswerte" },
    rangeKey() { return `${this.from}:${this.to}` },
    series() {
      const keys = this.topic === "movement" ? ["steps", "distanceMeters", "activeEnergyKilocalories", "totalEnergyKilocalories"]
        : ["sleepSeconds", "heartRate", "restingHeartRate", "hrvRmssdMillis", "oxygenSaturationPercentage"]
      return this.metrics.series.filter(item => keys.includes(item.metric) && item.method !== "mescan_report")
    },
    visibleSeries() { return this.series.filter(item => displayPreferences.visible(this.topic, item.metric)) },
    visibleBodyMetrics() { return BODY_METRICS.filter(item => displayPreferences.visible("body", item.key)).map(item => item.key) },
    chart() { return bodyChart((this.selectedSeries?.points || []).map(point => ({ ...point, time: point.time || new Date(`${point.day}T12:00:00`).getTime() }))) },
  },
  created() { mescanService.acquire() },
  mounted() { this.readHint(); this.reload() },
  beforeDestroy() { this.requestVersion++; mescanService.release() },
  watch: {
    rangeKey() { this.detailOpen = false; this.settingsOpen = false; this.reload() },
    topic() { this.detailOpen = false; this.settingsOpen = false; this.reload() },
    owner() { this.readHint(); this.detailOpen = false; this.settingsOpen = false; this.selectedSeries = null; this.reload() },
    allowed(value) { if (!value) { this.requestVersion++; this.detailOpen = false; this.settingsOpen = false; this.selectedSeries = null } },
    "health.permissionKey"() { this.detailOpen = false; this.selectedSeries = null; this.reload() },
    "health.enabled"() { this.reload() },
    "health.busy"(busy, previous) { if (previous && !busy) this.reload() },
  },
  methods: {
    format(metric, value) { return HEALTH_METRICS.some(item => item.key === metric) ? healthMetricValue(metric, value) : bodyValue(metric, value) },
    displayUnit(metric) { return (HEALTH_METRICS.find(item => item.key === metric) || BODY_METRICS.find(item => item.key === metric))?.unit || "" },
    date(value) { return this.$moment(value).format(typeof value === "number" ? "DD. MMM YYYY · HH:mm" : "DD. MMM YYYY") },
    hintKey() { return `roody:customer-hub:health-hint:${this.owner}` },
    readHint() { this.connectionHint.owner = this.owner; try { this.connectionHint.hidden = localStorage.getItem(this.hintKey()) === "hidden" } catch (_) { this.connectionHint.hidden = false } },
    dismissHint() { this.connectionHint.owner = this.owner; this.connectionHint.hidden = true; try { localStorage.setItem(this.hintKey(), "hidden") } catch (_) { /* Optional presentation preference only. */ } },
    async reload(force = false) {
      const version = ++this.requestVersion
      this.refreshing = false
      if (!this.allowed || this.topic === "body" || !this.android || !this.health.enabled || this.health.busy) return
      this.refreshing = true
      try { await adapter.loadMetricsRange(this.from, this.to, force === true) }
      finally { if (version === this.requestVersion) this.refreshing = false }
    },
    async openSettings() {
      if (!this.allowed || !this.android) return
      this.connectionNote = ""
      if (!this.health.supported) await healthConnect.refresh(true)
      if (this.health.supported) { this.detailOpen = false; this.$refs.connectionControls?.openSettings() }
      else this.connectionNote = "Für Health Connect brauchst du die aktuelle Android-App und ein unterstütztes Gerät. Deine Roody-Funktionen bleiben verfügbar."
    },
    async authorizeHistory() {
      if (!this.allowed) return
      await healthConnect.connect([...this.health.categories], true)
      if (this.allowed) this.reload()
    },
    openMetric(series) {
      if (!this.allowed) return
      if (["sleepSeconds", "heartRate"].includes(series.metric)) {
        this.detailOpen = false
        this.$router.push(customerHubHealthLocation(series.metric, series.latest.day)).catch(() => {})
        return
      }
      this.selectedSeries = series; this.selectedDay = series.latest.day; this.detailOpen = true
    },
  },
}
</script>

<style scoped>
.customer-health { color: #f4f5f5; padding: 18px 0; }
.customer-health--embedded { padding: 0; }
.customer-health header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; }
.customer-health header small { color: #a3c6c4; font-size: 10px; letter-spacing: 1px; }
.customer-health h3 { font-size: 21px; margin: 5px 0; }
.customer-health p { font-size: 13px; line-height: 1.6; }
.customer-health__hint { background: #ffffff06; padding: 16px; border: 1px solid #ffffff14; border-radius: 18px; }
.customer-health__quiet { color: #b7d6d0; font-size: 12px; text-decoration: underline; padding: 12px 6px; }
.customer-health__values { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.customer-health__values button { color: inherit; text-align: left; padding: 16px; display: flex; flex-direction: column; gap: 7px; min-width: 0; background: #ffffff06; border: 1px solid #ffffff12; border-radius: 17px; }
.customer-health__values button > span { font-size: 13px; }
.customer-health__values strong { font-size: 25px; font-variant-numeric: tabular-nums; }
.customer-health__values small, .customer-health__values time { color: #b8bfc0; font-size: 10px; line-height: 1.5; }
.customer-health__values strong small { font-size: 12px; }
.customer-health__note { color: #a8b2b1; font-size: 12px !important; margin-top: 16px; }
.customer-health__detail { background: #242828 !important; border-radius: 20px !important; }
.customer-health__chart { width: 100%; max-height: 240px; margin: 12px 0; }
.customer-health__chart polyline { fill: none; stroke: #a3c6c4; stroke-width: 2; }
.customer-health__chart circle { fill: #a3c6c4; }
.customer-health__records { list-style: none; padding: 0; max-height: 300px; overflow: auto; }
.customer-health__records li { display: flex; justify-content: space-between; gap: 16px; padding: 12px 0; border-bottom: 1px solid #ffffff14; font-size: 12px; }
.customer-health__records small { display: block; color: #999; }
.customer-health button:focus-visible { outline: 2px solid #a3c6c4; outline-offset: 3px; }
@media (max-width: 370px) { .customer-health__values { grid-template-columns: 1fr; } }
</style>
